import {
  acceptWaitMinutes,
  adjustedDueAt,
  assigneesOf,
  isNpc,
  isLeader,
  stateLabelOf,
} from '../model/derive';
import { KST, formatClock, formatDay, isMemberWorking, slotsOf, workWindowsOf } from '../model/time';
import { MIN, buildDashboard, dur, stagesFor } from '../model/dashboard';

/**
 * Ops › Live board — 서버가 준 팀 보드(읽기 전용 워크보드)에서 운영진 화면 값을 계산한다 (저장하는 값 없음).
 *
 *   수락 대기   배정 뒤 수락·거절 없이 흐른 시간 — 담당자 작업 시간 안에서만 센다. 30~59분 경고(Call) · 60분+ 실패(Reassign).
 *              담당자가 작업 시간 밖이면 "Wait paused · offline"
 *   열린 루프   막힘(Blocker) · 답 없는 질문 60분+(Question) · 반려 뒤 2시간 지난 수정 요청(Rework)
 *   이탈       담당자 이탈 → Reassign · 받는 사람 이탈 → Change receiver
 *   동료 평가   창이 열린 뒤 아직 제출하지 않은 팀원 — 마감 60분 전부터 Due soon · 지나면 Late · 닫히면 Not submitted
 *   참고       팀장(Ops)에게 제출돼 승인을 기다리는 태스크 — For reference (경고 없음)
 */

export const ACCEPT_WARN_MIN = 30;
export const ACCEPT_FAIL_MIN = 60;
export const QUESTION_WAIT_MIN = 60;
export const REWORK_WAIT_MIN = 120;
export const PEER_SOON_MIN = 60;

const HOUR = 60 * MIN;
const CLOSED = ['approved', 'cancelled'];
const ms = (iso) => (iso ? new Date(iso).getTime() : null);

export const hm = (t) => formatClock(new Date(t), KST);
export const dt = (t) => `${formatDay(new Date(t), KST)}, ${hm(t)}`;
export const kstAt = (t) => `${dt(t)} KST`;

/** 상태 칩 — 시안 CHIP */
export const STATE_CHIP = {
  assigned: { label: 'To accept', tone: 'neutral' },
  declined: { label: 'Declined', tone: 'warning' },
  in_progress: { label: 'In progress', tone: 'accent' },
  submitted: { label: 'Submitted', tone: 'accent' },
  rework_requested: { label: 'Changes requested', tone: 'warning' },
  approved: { label: 'Approved', tone: 'success' },
  cancelled: { label: 'Cancelled', tone: 'neutral' },
};
export const chipOf = (t) =>
  t.isFinal && t.state === 'submitted'
    ? { label: 'Final review', tone: 'accent' }
    : (STATE_CHIP[t.state] ?? { label: stateLabelOf(t), tone: 'neutral' });

/** 팀원 — 접속 아닌 작업 시간 기준 (Active · Offline · back HH:MM · Dropped) */
export const presenceOf = (m, now, ended = false) => {
  if (m.status === 'dropped') return { kind: 'dropped', label: 'Dropped' };
  if (ended) return { kind: 'offline', label: 'Project ended' };
  if (!m.timezone || !slotsOf(m).length || isMemberWorking(now, m)) return { kind: 'active', label: 'Active' };
  const back = workWindowsOf(now, m, 3).find(([s]) => s > now.getTime())?.[0];
  return { kind: 'offline', back: back ?? null, label: back ? `Offline · back ${hm(back)}` : 'Offline' };
};

/** 지금부터 종료까지 남은 작업 시간(분) — 작업 시간이 없으면 전부 */
const leftMinutes = (m, now, end) => {
  const t0 = now.getTime();
  if (end <= t0) return 0;
  if (!m.timezone || !slotsOf(m).length) return (end - t0) / MIN;
  return workWindowsOf(now, m, Math.ceil((end - t0) / (24 * HOUR)) + 1).reduce((sum, [s, e]) => {
    const a = Math.max(s, t0);
    const b = Math.min(e, end);
    return b > a ? sum + (b - a) / MIN : sum;
  }, 0);
};

/** 담당자 수락 대기 — { min, paused, lvl: null | 'warn' | 'fail' } */
export const acceptWaitOf = (task, now, member) => {
  const min = acceptWaitMinutes(task, now, member);
  if (min == null) return null;
  const paused = !!member && member.status !== 'dropped' && !isMemberWorking(now, member);
  return { min, paused, lvl: min >= ACCEPT_FAIL_MIN ? 'fail' : min >= ACCEPT_WARN_MIN ? 'warn' : null };
};

/** 열린 루프 — [{ kind: 'Question' | 'Blocker' | 'Rework', at, text, from, to }] */
export const openLoopsOf = (task, comments, now) => {
  const out = [];
  if (task.isBlocked) {
    const c = comments.find((x) => x.taskId === task.id && x.type === 'blocker' && !x.resolvedAt);
    out.push({ kind: 'Blocker', at: ms(c?.createdAt) ?? now.getTime(), text: c?.body ?? '', from: c?.authorId ?? task.assigneeId });
  }
  comments
    .filter((c) => c.taskId === task.id && c.type === 'question' && !c.parentId && !c.resolvedAt)
    .forEach((q) => {
      const answered = comments.some((r) => r.parentId === q.id && r.authorId === q.targetUserId);
      const age = (now.getTime() - ms(q.createdAt)) / MIN;
      if (!answered && age >= QUESTION_WAIT_MIN) out.push({ kind: 'Question', at: ms(q.createdAt), text: q.body, from: q.authorId, to: q.targetUserId });
    });
  if (task.state === 'rework_requested' && task.review?.at) {
    const age = (now.getTime() - ms(task.review.at)) / MIN;
    if (age >= REWORK_WAIT_MIN) out.push({ kind: 'Rework', at: ms(task.review.at), text: task.review.description ?? '', from: task.receiverId, to: task.assigneeId });
  }
  return out;
};

/** 동료 평가 줄 — 아직 제출하지 않은 팀원 [{ memberId, dueAt, lvl: 'soon' | 'late' | 'missed', label }] */
export const peerRowsOf = (peer, members, now) => {
  if (!peer?.opensAt || ms(peer.opensAt) > now.getTime()) return [];
  const t = now.getTime();
  return (peer.members ?? [])
    .filter((r) => !r.submittedAt)
    .map((r) => {
      const m = members.find((x) => x.id === r.memberId);
      if (!m || m.status === 'dropped') return null;
      const due = ms(r.dueAt);
      const closes = ms(peer.closesAt);
      if (closes && t >= closes) return { memberId: r.memberId, dueAt: due, lvl: 'missed', label: 'Not submitted · window closed' };
      if (due && t >= due) return { memberId: r.memberId, dueAt: due, lvl: 'late', label: `Late · ${dur((t - due) / MIN)} passed, open until the final due` };
      if (due && due - t <= PEER_SOON_MIN * MIN) return { memberId: r.memberId, dueAt: due, lvl: 'soon', label: `Due soon · ${dur((due - t) / MIN)} left` };
      return null;
    })
    .filter(Boolean);
};

/* ── 팀 하나 ─────────────────────────────────────────── */

/**
 * @param tb 서버 팀 { id, name, slug, createdAt, board, peerReview, members[{ id, droppedAt, dropReason }] }
 * @param logs Ops 기록 (call 시각을 Needs ops 줄에 보이려고)
 */
export const buildTeam = (tb, now, logs = []) => {
  const data = tb.board;
  const t0 = now.getTime();
  const END = ms(data.project.endAt);
  const ended = data.project.status === 'ended' || t0 >= END;
  const memberById = new Map(data.members.map((m) => [m.id, m]));
  const taskById = new Map(data.tasks.map((t) => [t.id, t]));
  const extra = new Map((tb.members ?? []).map((m) => [m.id, m]));
  const comments = data.comments ?? [];
  const name = (id) => (id === 'npc' || isNpc(memberById.get(id)) ? 'Team lead Kim' : (memberById.get(id)?.name ?? '–'));
  const first = (id) => name(id).split(' ')[0];
  const people = data.members.filter((m) => !isNpc(m));
  const leader = people.find(isLeader) ?? null;
  const calledAt = (taskId, memberId) =>
    logs.filter((l) => l.kind === (taskId ? 'call' : 'peer_call') && (taskId ? l.taskId === taskId : l.memberId === memberId)).map((l) => ms(l.createdAt)).sort((a, b) => b - a)[0] ?? null;

  const stages = stagesFor(data.tasks, memberById, t0);
  const stageOf = (taskId) => stages.find((s) => s.tasks.includes(taskId))?.name ?? null;

  const members = people.map((m) => {
    const p = presenceOf(m, now, ended);
    const open = data.tasks.filter((t) => assigneesOf(t).includes(m.id) && !CLOSED.includes(t.state));
    const cur = open.filter((t) => ['in_progress', 'rework_requested'].includes(t.state));
    const dropped = extra.get(m.id) ?? null;
    return {
      m,
      presence: p,
      leader: isLeader(m),
      tag: isLeader(m) ? 'Leader' : m.staff || m.staffBackup ? 'Staff' : null,
      current: cur[0] ?? open[0] ?? null,
      statusLabel: p.kind === 'dropped' ? `Dropped${dropped?.droppedAt ? ` · ${dt(dropped.droppedAt)}` : ''}` : p.label,
      now: p.kind === 'dropped' ? 'Out of peer review' : cur.length ? `${cur[0].title}${open.length > 1 ? ` · ${open.length} open` : ''}` : open.length ? `${open.length} open · none in progress` : 'No open tasks',
      dropped,
      inProgress: cur.length,
      leftMin: p.kind === 'dropped' ? 0 : leftMinutes(m, now, END),
    };
  });
  const memberRow = (id) => members.find((x) => x.m.id === id);

  const tasks = data.tasks.map((t) => {
    const assignee = memberById.get(t.assigneeId);
    const receiver = t.receiverId === 'npc' ? null : memberById.get(t.receiverId);
    const wait = acceptWaitOf(t, now, assignee);
    const loops = (CLOSED.includes(t.state) ? [] : openLoopsOf(t, comments, now)).map((l) => ({ ...l, fromName: name(l.from), toName: name(l.to) }));
    const adjusted = adjustedDueAt(t, taskById, now, data.project.endAt);
    const due = adjusted ? adjusted.getTime() : ms(t.dueAt);
    const assigneeDropped = !CLOSED.includes(t.state) && assignee?.status === 'dropped';
    const receiverDropped = !CLOSED.includes(t.state) && !!receiver && receiver.status === 'dropped';
    const toOps = t.receiverId === 'npc' || isNpc(memberById.get(t.receiverId));
    const called = calledAt(t.id);
    // 신호 — 점 + 글자 (시안 sigsOf)
    const sigs = [];
    if (t.isBlocked && !CLOSED.includes(t.state)) sigs.push({ label: 'Blocked', tone: 'failed' });
    if (wait && wait.paused && wait.min >= ACCEPT_WARN_MIN) sigs.push({ label: 'Wait paused · offline', tone: 'neutral' });
    else if (wait && wait.lvl) sigs.push({ label: `Waiting ${dur(wait.min)}`, tone: wait.lvl === 'fail' ? 'failed' : 'warning' });
    loops.filter((l) => l.kind !== 'Blocker').forEach((l) => sigs.push({ label: l.kind === 'Question' ? 'Open question' : 'Changes requested', tone: 'warning' }));
    if (toOps && t.state === 'submitted') sigs.push({ label: 'Approval flag', tone: 'neutral' });
    if (assigneeDropped) sigs.push({ label: 'Assignee dropped', tone: 'failed' });
    if (receiverDropped) sigs.push({ label: 'Receiver dropped', tone: 'failed' });
    if (!CLOSED.includes(t.state) && t.state !== 'submitted' && due && t0 > due) sigs.push({ label: `Overdue · ${dur((t0 - due) / MIN)}`, tone: 'failed' });
    const chips = [chipOf(t)];
    if (t.isBlocked && !CLOSED.includes(t.state)) chips.push({ label: 'Blocked', tone: 'failed' });
    if (t.extendedReason) chips.push({ label: 'Due changed', tone: 'neutral' });
    if (assigneeDropped) chips.push({ label: 'Assignee dropped', tone: 'failed' });
    return {
      t,
      chip: chipOf(t),
      chips,
      stage: stageOf(t.id),
      assignee,
      receiver,
      assigneeName: name(t.assigneeId),
      receiverName: name(t.receiverId),
      assigneeRow: memberRow(t.assigneeId) ?? null,
      receiverRow: receiver ? (memberRow(receiver.id) ?? null) : null,
      wait,
      called,
      loops,
      adjusted,
      due,
      dueT: dt(t.dueAt),
      adjT: adjusted ? `Adjusted ${dt(adjusted)}` : null,
      assigneeDropped,
      receiverDropped,
      toOps,
      sigs,
      signals: sigs,
    };
  });
  const taskRow = (id) => tasks.find((x) => x.t.id === id);

  // ── Needs ops — 오래된 것부터 (시안 needs)
  const needs = [];
  tasks.forEach((r) => {
    const t = r.t;
    const open = !['approved', 'cancelled', 'declined'].includes(t.state);
    if (r.wait?.lvl && !r.wait.paused && !r.assigneeDropped) {
      const over = r.wait.lvl === 'fail';
      needs.push({
        key: `wait:${t.id}`, tone: over ? 'failed' : 'warning', at: ms(t.assignedAt) ?? t0,
        title: `${over ? 'Accept wait over 60m' : 'Accept wait 30m+'} · ${t.title}`,
        meta: `${r.assigneeName} · waiting ${dur(r.wait.min)}${r.called ? ` · called ${hm(r.called)}` : ''}`,
        metaTone: over ? 'failed' : 'warning',
        taskId: t.id, memberId: t.assigneeId,
        cta: over ? { label: 'Reassign', action: 'reassign' } : r.called ? { label: 'View task', action: 'open' } : { label: 'Call', action: 'call' },
        primary: over || !r.called,
      });
    }
    if (open) {
      r.loops.forEach((l) => {
        const meta = l.kind === 'Question'
          ? `${name(l.from)} → ${name(l.to)} · open ${dur((t0 - l.at) / MIN)}`
          : l.kind === 'Blocker'
            ? `${r.assigneeName}${l.text ? ` · ${l.text}` : ''}`
            : `${r.receiverName} → ${r.assigneeName} · changes requested ${dur((t0 - l.at) / MIN)} ago`;
        needs.push({ key: `loop:${t.id}:${l.kind}`, tone: l.kind === 'Blocker' ? 'failed' : 'warning', at: l.at, kind: l.kind, title: `Open loop · ${t.title}`, meta, metaTone: 'secondary', taskId: t.id, cta: { label: 'View task', action: 'open' }, primary: false });
      });
      if (r.receiverDropped) {
        needs.push({ key: `drop-r:${t.id}`, tone: 'failed', at: ms(r.receiverRow?.dropped?.droppedAt) ?? t0, title: `Receiver dropped · ${t.title}`, meta: `${r.receiverName} is dropped · pick a new receiver`, metaTone: 'failed', taskId: t.id, cta: { label: 'Change receiver', action: 'receiver' }, primary: true });
      }
    }
    if (r.toOps && t.state === 'submitted') {
      needs.push({ key: `ref:${t.id}`, tone: 'info', at: ms(t.deliverable?.submittedAt) ?? t0, ref: true, title: `Approval flag · ${t.title}`, meta: `Submitted to Team lead Kim · waiting ${dur((t0 - (ms(t.deliverable?.submittedAt) ?? t0)) / MIN)} · review it in Lead inbox`, metaTone: 'secondary', taskId: t.id, cta: { label: 'View task', action: 'inbox' }, primary: false });
    }
  });
  members.filter((r) => r.presence.kind === 'dropped').forEach((r) => {
    const open = tasks.filter((x) => assigneesOf(x.t).includes(r.m.id) && !CLOSED.includes(x.t.state));
    if (!open.length) return;
    needs.push({ key: `drop-a:${r.m.id}`, tone: 'failed', at: ms(r.dropped?.droppedAt) ?? t0, title: `${r.m.name} is dropped · ${open.length} open task${open.length > 1 ? 's' : ''}`, meta: 'Reassign with reason Member dropout', metaTone: 'failed', taskId: open[0].t.id, memberId: r.m.id, cta: { label: 'Reassign', action: 'reassign' }, primary: true });
  });
  const peerRows = peerRowsOf(tb.peerReview, people, now);
  peerRows.forEach((p) => {
    const called = calledAt(null, p.memberId);
    const none = p.lvl === 'missed';
    needs.push({
      key: `peer:${p.memberId}`, tone: p.lvl === 'soon' ? 'warning' : p.lvl === 'late' ? 'failed' : 'neutral', at: p.dueAt ?? t0,
      title: `Peer review due soon · ${name(p.memberId)}`, meta: `${p.label}${called ? ` · called ${hm(called)}` : ''}`, metaTone: p.lvl === 'soon' ? 'warning' : p.lvl === 'late' ? 'failed' : 'secondary',
      memberId: p.memberId, cta: none ? { label: 'No action', action: 'none' } : { label: 'Call', action: 'peer_call' }, primary: !none && !called,
    });
  });
  needs.sort((a, b) => a.at - b.at);

  // ── Team progress (W7 Dashboard 재사용) + 인계 체인 상태
  const dash = buildDashboard({ data, now, memberById, needActions: [] });
  const firstOpen = stages.findIndex((s) => !s.d);
  const chain = stages.map((s, i) => {
    const adjMin = Math.max(0, s.delayIn + s.wait);
    const adj = adjMin ? Math.min(s.due + adjMin * MIN, END) : null;
    const ref = adj ?? s.due;
    const done = !!s.d;
    const late = done ? s.d > ref : t0 > ref && !s.rev;
    const active = !done && i === firstOpen;
    const status = done ? (late ? `Handed off ${dur((s.d - ref) / MIN)} late` : 'Handed off on time') : s.rev ? 'In review' : active ? (late ? `Late · ${dur((t0 - ref) / MIN)}` : 'In progress') : late ? `Late · ${dur((t0 - ref) / MIN)}` : 'Next';
    return { k: s.k, name: s.name, who: s.who.join(' · '), tasks: s.tasks, due: s.due, adjusted: adj, adjMin, status, late, done, active, recv: s.recv.join(', ') };
  });
  const live = data.tasks.filter((t) => t.state !== 'cancelled');
  const cnt = (ss) => live.filter((t) => ss.includes(t.state)).length;
  const approved = cnt(['approved']);
  const blocked = data.tasks.filter((t) => t.isBlocked && !CLOSED.includes(t.state)).length;
  const prog = {
    big: `${approved} of ${live.length} approved`,
    segs: [
      ['Approved', approved, 'bg-green-deep04'],
      ['In progress', cnt(['in_progress', 'submitted', 'assigned', 'declined']), 'bg-green-deep02'],
      ['Changes requested', cnt(['rework_requested']), 'bg-functional-warning03'],
    ].map(([l, n, c]) => ({ l, n, c, w: `${((n / Math.max(1, live.length)) * 100).toFixed(1)}%` })),
    blk: `Blocked overlaps the bar · ${blocked}`,
  };
  const workload = members.filter((r) => r.presence.kind !== 'dropped').map((r) => ({
    id: r.m.id,
    name: r.m.name,
    job: r.m.job,
    n: r.inProgress,
    left: r.presence.kind === 'active' ? `${dur(r.leftMin)} left until end` : `${r.presence.label} KST`,
    offline: r.presence.kind !== 'active',
  }));

  const counts = members.reduce((a, r) => ({ ...a, [r.presence.kind]: (a[r.presence.kind] ?? 0) + 1 }), { active: 0, offline: 0, dropped: 0 });
  const curTask = live.find((t) => t.state !== 'approved');
  const needsCount = needs.filter((n) => !n.ref).length;
  const summary = {
    leader: leader?.name ?? '–',
    stage: curTask ? `${stageOf(curTask.id) ?? (curTask.isFinal ? 'Final' : 'Task')} stage` : 'All approved',
    approved,
    denom: live.length,
    pct: live.length ? Math.round((approved / live.length) * 100) : 0,
    needs: needsCount,
    needsLabel: needsCount ? `${needsCount} need ops` : 'Nothing needs ops',
    blocked,
    ...counts,
    people: `${counts.active} active · ${counts.offline} offline${counts.dropped ? ` · ${counts.dropped} dropped` : ''}`,
  };

  return { team: tb, data, ended, end: END, memberById, taskById, name, first, members, memberRow, tasks, taskRow, needs, summary, dash, chain, prog, workload, peerRows, comments, stageOf };
};

/** 팀 순서 — 프로젝트 시작 → 팀 생성 → 승인 수 많은 순 → 종료 */
export const sortTeams = (teams) =>
  [...teams].sort(
    (a, b) =>
      (ms(a.data.project.startAt) ?? 0) - (ms(b.data.project.startAt) ?? 0) ||
      (ms(a.team.createdAt) ?? 0) - (ms(b.team.createdAt) ?? 0) ||
      b.summary.approved - a.summary.approved ||
      (ms(a.data.project.endAt) ?? 0) - (ms(b.data.project.endAt) ?? 0),
  );

/* ── 태스크 상세 (O3) ──────────────────────────────────── */

export const NOT_AFTER_APPROVAL = 'Not available after approval';
export const CANCELLED_TIP = 'Task is cancelled';
export const IN_REVIEW_TIP = 'Not available while in review';
export const NO_MEMBER_TIP = 'No eligible member';

/** 재배정 후보 — 같은 팀, 이탈 아님, 마케터 제외, 지금 담당자·받는 사람 제외 */
export const reassignCandidates = (row, members) =>
  members.filter((m) => !isNpc(m) && m.status !== 'dropped' && m.job !== 'marketer' && !assigneesOf(row.t).includes(m.id) && m.id !== row.t.receiverId);
export const receiverCandidates = (row, members) =>
  members.filter((m) => !isNpc(m) && m.status !== 'dropped' && m.job !== 'marketer' && !assigneesOf(row.t).includes(m.id) && m.id !== row.t.receiverId);

/**
 * 행동 가능 여부 — 상태 × 행동 표 (F-4 B: 검토 중 재배정 · 승인 뒤 취소는 막는다).
 * @returns { call: { show, label, primary }, reassign · due · receiver · cancel: { disabled, tip, primary } }
 */
export const actionsFor = (row, members = []) => {
  const { t, wait } = row;
  const closedTip = t.state === 'cancelled' ? CANCELLED_TIP : t.state === 'approved' ? NOT_AFTER_APPROVAL : null;
  const over = !!wait && wait.lvl === 'fail' && !wait.paused;
  const off = (tip, primary = false) => ({ disabled: !!tip, tip: tip ?? '', primary: !tip && primary });
  const reTip = closedTip ?? (t.state === 'submitted' ? IN_REVIEW_TIP : null) ?? (members.length && !reassignCandidates(row, members).length ? NO_MEMBER_TIP : null);
  const rcTip = closedTip ?? (members.length && !receiverCandidates(row, members).length ? NO_MEMBER_TIP : null);
  const callShow = t.state === 'assigned' && !!wait && wait.min >= ACCEPT_WARN_MIN && !wait.paused && !row.assigneeDropped;
  return {
    call: { show: callShow, label: row.called ? 'Call again' : `Call ${row.assigneeName.split(' ')[0]}`, primary: callShow && !over && !row.called },
    reassign: off(reTip, row.assigneeDropped || over),
    due: off(closedTip),
    receiver: off(rcTip, row.receiverDropped),
    cancel: off(closedTip),
  };
};

/** Hero — 우선순위: 닫힘 → 담당자 이탈 → 받는 사람 이탈 → 수락 대기 60+ → 30~59 → 열린 루프 → 할 일 없음 (시안 na) */
export const heroOf = (row) => {
  const { t, wait, loops } = row;
  const w = wait && !wait.paused ? wait : null;
  const closed = CLOSED.includes(t.state);
  if (closed) return { tone: 'closed', title: t.state === 'cancelled' ? 'This task is cancelled' : 'This task is approved', text: 'Closed tasks can’t be changed. Add a note below if you need to record something.', primary: null };
  if (row.assigneeDropped) return { tone: 'failed', title: 'Reassign this task', text: `${row.assigneeName} is dropped. Pick a new assignee; change the due with Member dropout if they need more time.`, primary: 'reassign' };
  if (row.receiverDropped) return { tone: 'failed', title: 'Receiver dropped · change receiver', text: `${row.receiverName} is dropped. Pick a new receiver so the next submission can be reviewed.`, primary: 'receiver' };
  if (w?.lvl === 'fail') return { tone: 'failed', title: `Accept wait ${dur(w.min)} · reassign this task`, text: `No accept or decline for over an hour. Reassign with “Accept no response”, or call ${row.assigneeName.split(' ')[0]} once more.`, primary: 'reassign' };
  if (w?.lvl === 'warn') return { tone: 'warning', title: row.called ? `Called ${hm(row.called)} · still waiting` : `Accept wait ${dur(w.min)} · call ${row.assigneeName.split(' ')[0]}`, text: 'The 2-day due keeps running while the task waits. A call posts a question to the assignee on this task.', primary: 'call' };
  if (loops.length) {
    const l = loops.find((x) => x.kind === 'Blocker') ?? loops[0];
    const desc = l.kind === 'Blocker' ? `${row.assigneeName} marked it blocked${l.text ? `: ${l.text}` : ''}.` : l.kind === 'Question' ? `${l.fromName ?? 'Someone'} asked ${row.assigneeName}${l.text ? `: ${l.text}` : ''}` : `${row.receiverName} requested changes.`;
    return { tone: 'warning', title: `Open loop · ${l.kind}`, text: `${desc} Watch it; step in only if it turns into a dropout signal.`, primary: null };
  }
  return { tone: 'ok', title: 'Nothing to do right now', text: 'Change due, reassign, change receiver and cancel are logged here and sent to the people involved.', primary: null };
};

/** 기한 늦추기 후보 — due + 2/4/6h, 종료 2시간 전까지 */
export const dueOptions = (task, endAt) => {
  const base = ms(task.dueAt);
  const cap = ms(endAt) - 2 * HOUR;
  return [2, 4, 6].map((h) => ({ h, at: Math.min(base + h * HOUR, cap) })).filter((o, i, a) => o.at > base && a.findIndex((x) => x.at === o.at) === i);
};

export const DUE_REASONS = [
  { key: 'system_outage', label: 'System outage', desc: 'A platform error blocked submitting, approving or another required step' },
  { key: 'member_dropout', label: 'Member dropout', desc: 'Covering or reassigning after a teammate dropped' },
  { key: 'admin_error', label: 'Brief error', desc: 'The brief was wrong and had to be resent or reassigned' },
];

export const DROP_RULES = [
  { key: 'missed_checkins', label: 'Missed 2 required check-ins in a row', desc: 'Checkpoint records show two misses back to back' },
  { key: 'no_answer', label: 'No answer for 2h+ in work hours', desc: 'To a question sent to them, ops calls included' },
  { key: 'said_leaving', label: 'Said they’re leaving', desc: 'They told the team or ops they’re out' },
];
